import moment from 'moment-timezone';
import {
  getDirtyAnswersReturnType,
  getDirtyAnswersType,
  getDirtyObjectsType,
  getDirtyValuesType,
  idType,
  selectItemType,
} from '@src/types';
import React, { SyntheticEvent } from 'react';
import { NormalizedTimeType } from '@component/Ride/types';
import { possibleBusinessTypes } from '@utils/possibleBusinessTypes';
import { addDays, addMinutes, format, isAfter, isBefore } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { TableData } from '@uikit/Tables/types';
import CryptoJS from 'crypto-js';
import { sessionSetter } from './storage';
import { FALLBACK_IMAGE, MASTER_KEY, X_HEADER_ID_NAME } from '@utils/constants';
import tzLookup from 'tz-lookup';
import { logger } from '@utils/logger';
import {
  FINALIZED_STATUS_IDS,
  NON_RIDESHARE_FINALIZED_STATUS_IDS,
  toastError,
} from '@utils/helpers';
import { toStateAbbreviation } from '@component/Locations/constants';
import { toastMessage } from '@utils/toastMessage';

export const checkBusinessType = (types: string[], name: string): string => {
  const found = types.some((type) => possibleBusinessTypes.includes(type));
  return found ? name : '';
};

const defaultServices = ['Ambulatory Curb to Curb', 'Ambulatory Door to Door'];

const businessNames = {
  premise: 'Residence',
  subpremise: 'Residential Unit',
};

export const stringToSelectObject = ({
  id,
  value,
}: {
  value: string;
  id: number;
}) => {
  return { value: value, label: value, id };
};

export const DEFAULT_NOTE_CATEGORY = {
  supervisor: {
    id: 35,
    value: '35',
    label: 'Supervisor override',
  },
  contractor: {
    id: 9,
    value: '9',
    label: 'Contractor general information modified',
  },
  member: {
    id: 15,
    value: '15',
    label: 'Member general information modified',
  },
  payer: {
    id: 18,
    value: '18',
    label: 'Payer general information modified',
  },
  monitoring: {
    id: 26,
    value: '26',
    label: 'Ride modified',
  },
  ride: {
    id: 26,
    value: '26',
    label: 'Ride modified',
  },
  standingOrder: {
    id: 33,
    value: '33',
    label: 'Standing order modified',
  },
  user: {
    id: 3,
    value: '3',
    label: 'User account modified',
  },
};

// TODO this is predefined city list, need to be changed
const neighborhoodLocations = new Set([
  'Los Angeles|CA',
  'New York|NY',
  'Chicago|IL',
  'Miami|FL',
  'San Diego|CA',
  'San Francisco|CA',
  'Seattle|WA',
  'Atlanta|GA',
  'Boston|MA',
  'Philadelphia|PA',
  'Washington|DC',
  'Portland|OR',
  'Houston|TX',
  'Phoenix|AZ',
  'Denver|CO',
]);

const getCityValue = (addressComponents: any) => {
  const state = handleLocationComponentsType(
    addressComponents,
    'administrative_area_level_1',
    'shortText'
  );
  const cityName = handleLocationComponentsType(addressComponents, 'locality');
  const isNeighborhood = neighborhoodLocations.has(`${cityName}|${state}`);
  const type = isNeighborhood ? 'neighborhood' : 'locality';

  return handleLocationComponentsType(addressComponents, type);
};

export const placeAddressInfo = (placeDetails: any) => {
  if (!placeDetails) return {};
  const unit =
    placeDetails.types.filter((type) => type === 'subpremise')?.[0] ?? '';
  const businessPossibleName =
    businessNames[placeDetails.primaryType] ??
    businessNames[unit] ??
    checkBusinessType(placeDetails.types, placeDetails.displayName);

  return {
    address1: handleLocationInputValue(
      [
        handleLocationComponentsType(
          placeDetails.addressComponents,
          'street_number'
        ),
        handleLocationComponentsType(placeDetails?.addressComponents, 'route'),
      ],
      false
    ),
    address2: handleLocationComponentsType(
      placeDetails.addressComponents,
      'subpremise'
    ),
    businessName: businessPossibleName,
    type: placeDetails.type,
    primaryType: placeDetails.primaryType,
    latitude: placeDetails.location.lat,
    longitude: placeDetails.location.lng,
    country: handleLocationComponentsType(
      placeDetails.addressComponents,
      'administrative_area_level_2'
    ),
    // TODO remove this after approval
    // city:
    //   handleLocationComponentsType(
    //     placeDetails.addressComponents,
    //     'neighborhood'
    //   ) ||
    //   handleLocationComponentsType(placeDetails.addressComponents, 'locality'),
    city:
      getCityValue(placeDetails.addressComponents) ||
      handleLocationComponentsType(placeDetails.addressComponents, 'locality'),
    state: handleLocationComponentsType(
      placeDetails.addressComponents,
      'administrative_area_level_1',
      'shortText'
    ),
    zipCode: handleLocationComponentsType(
      placeDetails.addressComponents,
      'postal_code'
    ),
    verified: false,
    phoneNumber: placeDetails.nationalPhoneNumber
      ? formatSymbolsPhoneNumberToString(placeDetails.nationalPhoneNumber)
      : '',
    phoneType: '',
    apiFrom: 'google',
    googlePlaceId: placeDetails.id,
  };
};

const formatSymbolsPhoneNumberToString = (input: string): string => {
  return input.replace(/\D/g, '');
};

export const handleLocationInputValue = (
  propsArray: Array<string | undefined>,
  hasComma: boolean = true
) => {
  return propsArray
    .filter((el) => {
      return el && el?.trim().length > 0;
    })
    .join(hasComma ? ', ' : ' ');
};

export const ensureString = (value: any) =>
  (typeof value === 'string' && value) || '';

export const handleLocationComponentsType = (
  components: Array<{
    longText: string;
    shortText: string;
    types: Array<string>;
  }> = [],
  key: string,
  nameType: 'shortText' | 'longText' = 'longText'
) => {
  return components.find((comp) => comp.types.includes(key))?.[nameType] || '';
};

export const getModelId = {
  user: 1,
  contractor: 2,
  member: 3,
  payor: 4,
  ride: 5,
  standingOrder: 6,
};

export const linkTo = (hrefName: string) => {
  switch (hrefName) {
    case 'payor':
    case 'Payors':
      return '/payors/information';
    case 'member':
    case 'Members':
      return '/members/information';
    case 'admin':
    case 'Admin':
      return '/administrator/users';
    case 'place':
    case 'Places':
      return '/places/information';
    case 'ride':
    case 'Rides':
      return '/rides/information';
    case 'standingOrder':
    case 'StandingOrders':
      return '/standing-order/details';
    case 'contractor':
    case 'Contractors':
      return '/contractors/information';
    case 'assignment':
    case 'Assignment':
      return '/assignment/information';
    default:
      return '/';
  }
};

export const labelTitleConverter = {
  payor: 'Payors',
  member: 'Members',
  place: 'Places',
  admin: 'Admin',
  user: 'Admin',
  contractor: 'Contractors',
  ride: 'Rides',
  dashboard: 'Dashboard',
  reporting: 'Dashboard',
  monitoring: 'Monitoring',
  standingOrder: 'StandingOrder',
  assignment: 'Assignments',
  contractorPortal: 'ContractorPortal',
  home: '/',
  profile: '/',
};

interface ModulePermission {
  name: string;
  permissions: any;
}

type Ability = { id: number; name: string };
type Permission = { abilities: Ability[]; [key: string]: any };
type Module = { id: number; name: string; permissions: Permission[] };

export const getModulePermissions = (modules: Module[]) => {
  if (!modules) return [];

  return modules.map((mod) => {
    const abilities =
      mod?.permissions?.flatMap((perm) =>
        perm.abilities.map((a: any) => (typeof a === 'string' ? a : a.name))
      ) ?? [];

    return {
      module: mod.name,
      abilities: [...new Set(abilities)],
    };
  });
};

export const newModulePermissions = (
  array: ModulePermission[] | undefined,
  storageActiveUrl: string
): any => {
  if (!array) return [];
  if (storageActiveUrl === '/') {
    return array;
  }

  const exactPermission = array.find((item) => item.name === storageActiveUrl);

  return exactPermission ? exactPermission.permissions : [];
};

export const getNewAbilities = (title: string, permissions: any) => {
  const permission = permissions?.find((item) => item.name === title);
  return {
    abilities:
      (permission &&
        permission.hasOwnProperty('abilities') &&
        permission?.abilities.map((access) => access.name)) ??
      [],
    [title]: permission ? [permission] : [],
  };
};

type beautifyDateTimeType = (
  inputDate: string | null,
  mode?: 'dateAndTime' | 'dateOnly',
  format?: string
) => string | Date | null;

export const beautifyDateTime: beautifyDateTimeType = (
  inputDate,
  mode = 'dateAndTime',
  format = 'MM/DD/YYYY HH:mm z'
): string => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (inputDate) {
      const timeWithMillisecondsRegex = /^\d{2}:\d{2}:\d{2}(\.\d+)?$/;
      if (timeWithMillisecondsRegex.test(inputDate)) {
        const timeOnly = inputDate.split('.')[0];
        return moment
          .tz(timeOnly, 'HH:mm:ss', true, timeZone)
          .format('HH:mm z');
      }

      const parsedDate = moment.tz(
        inputDate,
        [
          moment.ISO_8601,
          'YYYY-MM-DD HH:mm:ss',
          'YYYY/MM/DD HH:mm:ss',
          'YYYY/MM/DD',
          'MM/DD/YYYY',
          'HH:mm:ss',
        ],
        true,
        timeZone
      );

      if (!parsedDate.isValid()) {
        return '';
      }

      if (
        (parsedDate.hours() === 0 &&
          parsedDate.minutes() === 0 &&
          parsedDate.seconds() === 0) ||
        mode === 'dateOnly'
      ) {
        return parsedDate.format(format.replace(' HH:mm z', '')); // Return only the date
      }

      return parsedDate.format(format);
    } else {
      return '';
    }
  } catch (err: any) {
    toastError(err, toastMessage.COMMON.failedToFormatDate);
    return '';
  }
};

export const addFieldsConditionally = <T extends Record<string, any>>(
  fields: Partial<T>
): Partial<T> => {
  return Object.entries(fields).reduce((acc, [key, value]) => {
    if (value) {
      acc[key as keyof T] = value;
    }
    return acc;
  }, {} as Partial<T>);
};

export const googlePlaceTypesMapping = (
  places: any[],
  fieldName: 'address1' | 'city' | 'state' | 'zipCode'
) => {
  const validPlaces = {
    address1: ['street_address', 'premise', 'subpremise', 'route'],
    city: ['locality', 'sublocality'],
    state: [
      'administrative_area_level_1',
      'administrative_area_level_2',
      'administrative_area_level_3',
      'administrative_area_level_4',
    ],
    zipCode: ['postal_code', 'post_office'],
  };

  return validPlaces[fieldName].some((element) => places.includes(element));
};

export const createBodySampleForGoogle = (fields, array, value) => {
  return fields.reduce((acc, field) => {
    return {
      ...acc,
      ...addFieldsConditionally({
        [field]: googlePlaceTypesMapping(array, field) ? value : '',
      }),
    };
  }, {});
};

export const defaultObjectField = (addressFields: any, value: string) => {
  if (addressFields && Object.keys(addressFields).length === 0) {
    return { address1: value };
  }
  return addressFields;
};

export const formatSelectInputs = (
  changedValues: Record<string, any>,
  selectFields?: string[]
) => {
  if (!selectFields) return changedValues;
  selectFields.forEach((key) => {
    if (changedValues[key]) {
      if (Array.isArray(changedValues[key])) {
        changedValues[key] = changedValues[key].map((item) => item.id);
      } else {
        changedValues[key] = changedValues[key].id;
      }
    }
  });

  return changedValues;
};

export const formatBooleanfields = (
  requiredValuesFields: Record<string, any>,
  changedValues: Record<string, any>,
  values: Record<string, any>
) => {
  if (requiredValuesFields && Boolean(requiredValuesFields.length)) {
    requiredValuesFields.forEach((key) => {
      changedValues[key] = values[key];
    });
  }

  return changedValues;
};

export const getDirtyValues = ({
  dirtyFields,
  values,
  maskedInputs = [],
  defaultValues,
  selectFields,
  requiredValuesFields = [],
}: getDirtyValuesType) => {
  const changedValues: Record<string, any> = {};

  formatBooleanfields(requiredValuesFields, changedValues, values);

  Object.keys(dirtyFields).forEach((key) => {
    if (dirtyFields[key]) {
      const newValue = maskedInputs.includes(key)
        ? String(values[key])?.replace(/\D/g, '')
        : values[key];
      if (defaultValues) {
        changedValues[key] = newValue;
      }
    }
  });

  return formatSelectInputs(changedValues, selectFields);
};

export const getDirtyObjects = ({
  values,
  defaultValues,
  objectFields,
}: getDirtyObjectsType) => {
  const changedValues: Record<string, any> = {};

  objectFields?.forEach((key) => {
    if (defaultValues && defaultValues[key]?.id !== values[key]?.id) {
      changedValues[key] = values[key]?.id;
    }
  });

  return changedValues;
};

export const collectDirtyFieldPaths = (
  dirtyFields: unknown,
  values: unknown
): Record<string, any> => {
  const result: Record<string, any> = {};

  const walk = (dirtyNode: unknown, valueNode: unknown, path: string) => {
    if (!dirtyNode) return;

    if (dirtyNode === true) {
      result[path] = valueNode;
      return;
    }

    if (Array.isArray(dirtyNode)) {
      dirtyNode.forEach((item, index) => {
        walk(item, (valueNode as any)?.[index], `${path}.${index}`);
      });
      return;
    }

    if (typeof dirtyNode === 'object') {
      Object.entries(dirtyNode as Record<string, unknown>).forEach(
        ([key, item]) => {
          walk(item, (valueNode as any)?.[key], path ? `${path}.${key}` : key);
        }
      );
    }
  };

  if (dirtyFields && typeof dirtyFields === 'object') {
    Object.entries(dirtyFields as Record<string, unknown>).forEach(
      ([key, item]) => {
        walk(item, (values as any)?.[key], key);
      }
    );
  }

  return result;
};

export const getDirtyAnswers = ({
  // dirtyFields,
  values,
}: getDirtyAnswersType): getDirtyAnswersReturnType => {
  const changedValues: getDirtyAnswersReturnType = [];

  const handleAnswerType = (
    answerType: 'Multiple choice' | 'Select' | 'Freeform',
    answer: selectItemType | Array<selectItemType> | string
  ): string => {
    switch (answerType) {
      case 'Multiple choice':
        return (answer as Array<selectItemType>).map((el) => el.label).join();
      case 'Select':
        return (answer as selectItemType).label;
      case 'Freeform':
        return answer as string;
      default:
        return '';
    }
  };

  // TODO this need if we want send only changed answers
  // Object.keys(dirtyFields).forEach((key) => {
  //   if (dirtyFields[key]) {
  //     const { answerType, answer, surveyTemplateId, id } = values[key];
  //
  //     changedValues.push({
  //       answer: handleAnswerType(answerType, answer),
  //       templateId: surveyTemplateId,
  //       questionId: id,
  //     });
  //   }
  // });

  values.forEach((el) => {
    const { answerType, answer, surveyTemplateId, id } = el;
    changedValues.push({
      answer: handleAnswerType(answerType, answer),
      templateId: surveyTemplateId,
      questionId: id,
    });
  });

  return changedValues;
};

export const getRowsPerPageOptions = [
  {
    id: 1,
    label: '5',
    value: '5',
  },
  {
    id: 2,
    label: '10',
    value: '10',
  },
  {
    id: 3,
    label: '20',
    value: '20',
  },
  {
    id: 4,
    label: '50',
    value: '50',
  },
  {
    id: 5,
    label: '100',
    value: '100',
  },
  {
    id: 6,
    label: '500',
    value: '500',
  },
];

export const getAdvancedRowsPerPageOptions = (totalCount: number) => [
  {
    id: 1,
    label: '100',
    value: '100',
  },
  {
    id: 2,
    label: '250',
    value: '250',
  },
  {
    id: 3,
    label: '500',
    value: '500',
  },
  {
    id: 4,
    label: '750',
    value: '750',
  },
  {
    id: 5,
    label: '1000',
    value: '1000',
  },
  // {
  //   id: 6,
  //   label: 'All',
  //   value: `${totalCount}`,
  // },
];

interface Role {
  name: string;
  modelId: number;
  id: number;
}

export const configsNotesIdByPath: Role[] = [
  { name: 'user', modelId: 1, id: 3 },
  { name: 'contractor', modelId: 2, id: 9 },
  { name: 'member', modelId: 3, id: 15 },
  { name: 'payor', modelId: 4, id: 18 },
  { name: 'ride', modelId: 5, id: 26 },
  { name: 'standingOrder', modelId: 6, id: 33 },
  // TODO this is just temporary solution, need to be fixed
  { name: 'monitoring', modelId: 5, id: 26 },
  { name: 'assignment', modelId: 5, id: 26 },
];

export const formatRelativeTime = (
  dateString: string | null | undefined | Date
) => {
  if (!dateString) return '';

  const date = moment.utc(dateString).tz('America/Los_Angeles');
  const now = moment.tz('America/Los_Angeles');

  if (!date.isValid()) return '';

  const diffMinutes = now.diff(date, 'minutes');

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} min`;

  const diffHours = now.diff(date, 'hours');
  if (diffHours < 24) return `${diffHours}h`;

  const diffDays = now.diff(date, 'days');
  if (diffDays < 7) return `${diffDays}d`;

  return date.format('MM/DD/YYYY');
};

export const formatTimeDifference = (date: Date | string): string => {
  const parseToMoment = (d: Date | string) => {
    if (d instanceof Date) return moment(d);
    const hasTz = /Z$|[+-]\d{2}:\d{2}$/.test(d);
    return hasTz ? moment.parseZone(d) : moment.utc(d);
  };

  const input = parseToMoment(date);
  if (!input.isValid()) return '';

  const now = moment();
  const diffMs = Math.max(0, now.diff(input));
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  const diffMinutes = Math.floor(diffMs / minuteMs);
  if (diffMinutes < 1) return 'now';
  if (diffMinutes < 60) return `${diffMinutes}m`;

  const diffHours = Math.floor(diffMs / hourMs);
  if (diffHours < 24) return `${diffHours}h`;

  const diffDays = Math.floor(diffMs / dayMs);
  if (diffDays < 7) return `${diffDays}d`;

  return input.clone().local().format('MM/DD/YYYY');
};

export const formatPhoneNumber = (phoneNumber?: string): string => {
  if (!phoneNumber) return '';
  const cleaned = phoneNumber.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (match) {
    const areaCode = match[1];
    const centralOfficeCode = match[2];
    const lineNumber = match[3];
    let formatted = '';
    if (areaCode) formatted += `(${areaCode}`;
    if (centralOfficeCode)
      formatted += areaCode ? `) ${centralOfficeCode}` : `${centralOfficeCode}`;
    if (lineNumber) formatted += areaCode ? `-${lineNumber}` : ` ${lineNumber}`;
    return formatted.trim();
  }

  return phoneNumber;
};

export const rgbToHex = (rgb: string) => {
  if (!rgb) return '#000000';

  if (rgb.startsWith('#')) return rgb;

  const result = rgb.match(/\d+/g);
  if (!result || result.length < 3) return '#000000';

  return `#${result
    .slice(0, 3)
    .map((num) => parseInt(num, 10).toString(16).padStart(2, '0'))
    .join('')}`.toUpperCase();
};

export const getLocationAsText = (location, showBusinessName = true) => {
  return handleLocationInputValue([
    (showBusinessName ? ensureString(location?.businessName) : '') ?? '',
    ensureString(location?.address1),
    ensureString(location?.address2),
    ensureString(location?.city?.value) || ensureString(location?.city),
    toStateAbbreviation(location?.state),
    ensureString(location?.zipCode?.value) || ensureString(location?.zipCode),
  ]);
};

export const checkSpokenLanguage = (language: string) => {
  const languages = {
    English: 'en',
    Spanish: 'es',
    French: 'fr',
    Portuguese: 'pt',
  };

  return languages[language] || 'en';
};

export const formatUTCToLocal = (
  utcDate: string | number | Date,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string => {
  let date: Date;

  if (typeof utcDate === 'string') {
    date = new Date(utcDate.endsWith('Z') ? utcDate : utcDate + 'Z');
  } else {
    date = new Date(utcDate);
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value || '';

  return `${get('month')}/${get('day')}/${get('year')} ${get('hour')}:${get('minute')} ${get('timeZoneName')}`;
};

export const formatTime = (
  totalSeconds: number,
  showSeconds: boolean = true
): string => {
  if (showSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [hours, minutes, seconds]
      .map((unit) => String(unit).padStart(2, '0'))
      .join(':');
  }

  const totalMinutes = Math.round(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return [hours, minutes]
    .map((unit) => String(unit).padStart(2, '0'))
    .join(':');
};

export const timeToSeconds = (time: string) => {
  const [hours = 0, minutes = 0, seconds = 0] = time?.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export const sumTimes = (times: string[]): number => {
  return times.reduce((total, time) => total + timeToSeconds(time), 0);
};

export const handleImgError = (event: SyntheticEvent<HTMLImageElement>) => {
  const imgElement = event.currentTarget;
  if (imgElement.src !== FALLBACK_IMAGE) {
    imgElement.src = FALLBACK_IMAGE;
  }
};

export const createCustomMarker = (
  labelText: string,
  fillColor: string,
  {
    fontSize = '10',
    fontFamily = 'Arial',
    textColor = 'black',
  }: { fontSize?: string; fontFamily?: string; textColor: string }
) => {
  const svgMarker = `
    <svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Marker Shape with Dynamic Fill -->
      <path d="M15.9984 3.7002C11.2784 3.7002 6.39844 7.3002 6.39844 13.3002C6.39844 18.9802 15.0384 27.8602 15.4384 28.2602C15.5984 28.4202 15.7584 28.5002 15.9984 28.5002C16.2384 28.5002 16.3984 28.4202 16.5584 28.2602C16.9584 27.8602 25.5984 19.0602 25.5984 13.3002C25.5984 7.3002 20.7184 3.7002 15.9984 3.7002ZM15.9984 16.5002C14.2384 16.5002 12.7984 15.0602 12.7984 13.3002C12.7984 11.5402 14.2384 10.1002 15.9984 10.1002C17.7584 10.1002 19.1984 11.5402 19.1984 13.3002C19.1984 15.0602 17.7584 16.5002 15.9984 16.5002Z" fill="${fillColor}"/>

      <!-- Label Background -->
      <rect x="10" y="8" width="12" height="12" rx="6" ry="6" fill="#292850"/>

      <!-- Label Text -->
      <text x="16" y="18" font-size="${fontSize}" font-family="${fontFamily}" font-weight="bold" text-anchor="middle" fill="${textColor}">${labelText}</text>
    </svg>
  `;

  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgMarker),
    scaledSize: new google.maps.Size(32, 33),
    anchor: new google.maps.Point(16, 33), // Adjust if needed
  };
};

export const createCustomSingleMarker = () => {
  const svgMarker = `
  <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="-6 -6 44 44">
  <circle cx="16" cy="16" r="22" fill="#E25A13" />
  <path data-color="color-2" d="M31,15c-.227,0-.448-.077-.625-.219L16,3.28,1.625,14.781c-.431,.345-1.061,.275-1.406-.156s-.275-1.061,.156-1.406h0L15.375,1.219c.365-.293,.885-.293,1.25,0l15,12c.431,.345,.501,.974,.156,1.406-.19,.237-.477,.376-.781,.375Z" fill="#FFFFFFFF"/>
  <path d="M16,5.842L4,15.442v13.058c0,1.381,1.119,2.5,2.5,2.5h6.5v-7c0-.552,.448-1,1-1h4c.552,0,1,.448,1,1v7h6.5c1.381,0,2.5-1.119,2.5-2.5V15.442L16,5.842Z" fill="#FFFFFFFF"/>
</svg>
  `;

  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgMarker),
    scaledSize: new google.maps.Size(32, 33),
    anchor: new google.maps.Point(16, 33),
  };
};

export const createMarker = (
  position: any,
  label: string,
  color: string,
  title: string
) => ({
  position: {
    lat: position.latitude,
    lng: position.longitude,
  },
  icon: createCustomMarker(label, color, {
    textColor: 'white',
    fontSize: '11px',
  }),
  title: `${title} Point`,
});

export const createSingleMarker = (
  position: any,
  withoutIcon: boolean = false
) => ({
  position: {
    lat: position.latitude,
    lng: position.longitude,
  },
  icon: withoutIcon ? undefined : createCustomSingleMarker(),
  title: 'Waypoint 1',
});

export const getFiltrationValues = (filterTable, index = 0) => {
  const keys = Object.keys(filterTable);
  return filterTable?.[keys[index]];
};

export const capitalizeWords = (str: string): string =>
  str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

export const addSeconds = (timeStr: string): string => {
  const timePattern = /^([0-1]?[0-9]|2[0-3]):([0-5]?[0-9])$/;
  if (!timePattern.test(timeStr)) {
    return '';
  }
  return `${timeStr}:00`;
};

export const addServiceType = (obj: any, id: any) => {
  const isChosen = !obj.hasOwnProperty('approvedService')
    ? // ? { approvedService: false }
      { approvedService: true }
    : {};

  const isCreateObject = id ? {} : isChosen;

  return {
    ...obj,
    ...isCreateObject,
  };
};

interface DateComponents {
  month: string;
  day: number;
  year: number;
  weekday: string;
}

// Map of weekday numbers to their abbreviations
const weekdayAbbreviations: Record<number, string> = {
  0: 'M',
  1: 'TU',
  2: 'W',
  3: 'TH',
  4: 'F',
  5: 'SA',
  6: 'SU',
};

const weekDayConverterCache = new Map<string, string>();

export const weekDayFirstLetterConverter = (
  weekday: number[] | undefined,
  single: boolean = false
) => {
  if (!weekday || weekday.length === 0) return 'no data';

  const cacheKey = weekday.toString();

  if (weekDayConverterCache.has(cacheKey)) {
    return weekDayConverterCache.get(cacheKey)!;
  }

  const result = [...weekday]
    .sort((a, b) => a - b)
    .map((day) =>
      !single ? weekdayAbbreviations[day] : weekdayAbbreviations[day].charAt(0)
    )
    .join(' ');

  weekDayConverterCache.set(cacheKey, result);

  return result;
};

export const getDateComponents = (dateString?: string): DateComponents => {
  if (!dateString) {
    return {
      month: '',
      day: 0,
      year: 0,
      weekday: '',
    };
  }
  const [month, day, year] = dateString.split('/').map(Number);

  const date = new Date(year, month - 1, day);

  const monthOptions: Intl.DateTimeFormatOptions = { month: 'short' };
  const weekdayOptions: Intl.DateTimeFormatOptions = { weekday: 'short' };

  const formattedMonth = date.toLocaleDateString('en-US', monthOptions);
  const formattedWeekday = date.toLocaleDateString('en-US', weekdayOptions);

  return {
    month: formattedMonth,
    day: day,
    year: year,
    weekday: formattedWeekday,
  };
};

/*TODO this should be a separate hook*/
export const normalizeTime = (
  inputTime: string = '00:00', // time format 00:00
  selectedDate: string = '12/12/2000' // date format  MM/DD/YYYY
): NormalizedTimeType => {
  const [inputHours, inputMinutes] = inputTime.split(':').map(Number);

  const [month, day, year] = selectedDate.split('/').map(Number);
  const selectedDateObj = new Date(year, month - 1, day);

  const totalMinutes = inputHours * 60 + inputMinutes;

  const normalizedHours = Math.floor(totalMinutes / 60) % 24;
  const normalizedMinutes = totalMinutes % 60;

  const daysToAdd = Math.floor(totalMinutes / (24 * 60));

  const formattedHours = String(normalizedHours).padStart(2, '0');
  const formattedMinutes = String(normalizedMinutes).padStart(2, '0');

  const nextDateObj = new Date(selectedDateObj);
  nextDateObj.setDate(selectedDateObj.getDate() + daysToAdd);

  const nextDate = `${String(nextDateObj.getMonth() + 1).padStart(2, '0')}/${String(nextDateObj.getDate()).padStart(2, '0')}/${nextDateObj.getFullYear()}`;

  // /*TODO do i need manually add weekdays?? check*//
  const weekdays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  const nextWeekday = weekdays[nextDateObj.getDay()];

  return {
    // /*TODO add real seconds if we need them in future*//
    normalizedTime: `${formattedHours}:${formattedMinutes}`,
    daysToAdd,
    nextDate,
    nextWeekday,
  };
};

export const addOneDay = (dateStr: string): string => {
  const [month, day, year] = dateStr.split('/').map(Number);

  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + 1);

  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();

  return `${mm}/${dd}/${yyyy}`;
};

export const isValidTime = (timeStr: string): boolean => {
  const regex = /^([01]\d|2[0-3]):([0-5]\d)(:[0-5]\d)?$/;
  return regex.test(timeStr);
};

export const findLocationData = (
  inputText: string,
  data: { id: number; value: string; label: string }[],
  type: 'county' | 'city' | 'zipCode'
) => {
  const searchLabel = inputText.toLowerCase();
  return (
    data.find(
      (item) =>
        searchLabel.includes(item.label.toLowerCase()) ||
        searchLabel.includes(item.value.toLowerCase())
    ) ?? ''
  );
};

export const addOrSubtractDuration = (
  currentTime: string | null,
  durations: Array<string> = ['00:00'],
  add: boolean = false
): {
  nextDay: boolean;
  time: string;
} => {
  const [currentHours, currentMinutes] = (currentTime || '00:00')
    .split(':')
    .map(Number);

  const totalCurrentMinutes = currentHours * 60 + currentMinutes;
  let totalDurationMinutes = 0;

  durations.forEach((duration) => {
    const [durationHours, durationMinutes] = duration
      ? duration.split(':').map(Number)
      : '00:00'.split(':').map(Number);
    totalDurationMinutes += durationHours * 60 + durationMinutes;
  });

  const newTotalMinutes = add
    ? totalCurrentMinutes + totalDurationMinutes
    : totalCurrentMinutes - totalDurationMinutes;

  const nextDay = Math.floor(newTotalMinutes / 60) >= 24 || newTotalMinutes < 0;
  let newHours = Math.floor(newTotalMinutes / 60) % 24;
  newHours = newHours >= 0 ? newHours : newHours + 24;
  let newMinutes = newTotalMinutes % 60;
  newMinutes = newMinutes >= 0 ? newMinutes : newMinutes + 60;

  const formattedHours = newHours.toString().padStart(2, '0');
  const formattedMinutes = newMinutes.toString().padStart(2, '0');

  return {
    nextDay,
    time: `${formattedHours}:${formattedMinutes}`,
  };
};

export const removeComma = (value: string = '') => {
  return `${!!value.trim() ? value + ', ' : ''}`;
};

export const getMsFromTime = (
  date: string = format(new Date(), 'MM/DD/YYYY'),
  time: string = '00:00'
) => {
  const [month, day, year] = date.split('/').map(Number);
  const [hours, minutes] = time.split(':').map(Number);

  return new Date(year, month - 1, day, hours, minutes).getTime();
};

export const checkLocationSpecificDates = (
  dateString: string,
  timeString: string,
  lat: number,
  lng: number
) => {
  const timeZone = tzLookup(lat, lng);

  const [month, day, year] = dateString.split('/');
  const dateTimeStr = `${year}-${month}-${day} ${timeString}:00`;

  const selectedDateUtc = fromZonedTime(dateTimeStr, timeZone);

  const nowUtc = new Date();
  const fifteenMinsFromNow = addMinutes(nowUtc, 15);
  const thirtyDaysFromNow = addDays(nowUtc, 30);

  return {
    isLaterThan15MinutesFromNow: isAfter(selectedDateUtc, fifteenMinsFromNow),
    isWithin30DaysFromNow:
      isAfter(selectedDateUtc, nowUtc) &&
      isBefore(selectedDateUtc, thirtyDaysFromNow),
  };
};

export const redirectToExternalUrl = (url?: string, newTab = false) => {
  if (!url) return;

  try {
    const validUrl = new URL(url);
    if (newTab) {
      window.open(validUrl.toString(), '_blank');
    } else {
      window.location.href = validUrl.toString();
    }
  } catch (error) {
    logger.error('Invalid URL provided:', url);
  }
};

export const formattedTime = (dateString: string) => {
  if (!dateString) return '';
  let newDate = dateString;
  if (newDate.endsWith('Z')) {
    newDate = newDate.slice(0, -1);
  }
  // const date = new Date(dateString);
  const date = new Date(newDate);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export const formattedWebhookTime = (dateString: string, timezone?: string) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone ?? 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

export const formatEtaToTime = (
  etaStartTimestamp: number,
  etaSeconds?: number | null,
  startEtaDate?: boolean,
  timeZone: string = 'UTC'
) => {
  if (!etaSeconds) return '';

  const arrivalMs = startEtaDate
    ? Date.now() + etaSeconds * 1000
    : etaStartTimestamp + etaSeconds * 1000;

  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(arrivalMs));
};

export const convertDateExact = (dateString: string) => {
  const [year, month, day] = dateString.split('-');
  return `${month}/${day}/${year}`;
};

export const formatMonthOrDate = (input: string): string => {
  const parts = input.split('-');

  if (parts.length === 3) {
    return convertDateExact(input);
  }

  if (parts.length === 2) {
    const [year, month] = parts;

    const yearNum = Number(year);
    const monthNum = Number(month);

    const firstDateString = `${year}-${month}-01`;

    const lastDay = new Date(yearNum, monthNum, 0).getDate();
    const lastDateString = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

    return `${convertDateExact(firstDateString)} - ${convertDateExact(lastDateString)}`;
  }

  throw new Error('Invalid format. Use YYYY-MM or YYYY-MM-DD');
};

export const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const { key, currentTarget } = e;
  const value = currentTarget.value;
  const cursorPosition = currentTarget.selectionStart ?? 0;

  const allowedNavigationKeys = [
    'Backspace',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    'Delete',
  ];

  if (allowedNavigationKeys.includes(key)) return;

  if (/[0-9]/.test(key)) return;

  if (key === '.') {
    if (value.includes('.') || cursorPosition === 0) {
      e.preventDefault();
    }
    return;
  }

  if (key === '-') {
    if (cursorPosition !== 0 || value.includes('-')) {
      e.preventDefault();
    }
    return;
  }

  e.preventDefault();
};

export const removeUSCountryNames = (text: string): string => {
  const usVariants = [
    'USA',
    'U.S.',
    'U.S',
    'US',
    'United States of America',
    'United States',
  ];

  const pattern = new RegExp(
    `\\s*,?\\s*\\b(${usVariants.join('|').replace(/\./g, '\\.')})\\b\\s*,?\\s*`,
    'gi'
  );

  return text
    .replace(pattern, '')
    .replace(/\s*,\s*/g, ', ')
    .replace(/,\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const formatWeightInput = (value: string): string => {
  const numeric = value.replace(/[^\d]/g, '');
  return numeric.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const setStatusValueFromLabel = (
  object: selectItemType
): selectItemType => {
  if (!object.value) {
    return {
      ...object,
      value: object.label,
    };
  }
  return object;
};

export const getTableParams = (tableData: TableData) => {
  return {
    page: tableData.page,
    size: tableData.size,
    sortBy: tableData.sortBy,
    sortDir: tableData.sortDir,
  };
};

export const getDateFromString = (
  date: string = '12/12/2000',
  time: string = '00:00'
) => {
  try {
    const [mm = '12', dd = '12', yyyy = '2000'] = date.split('/');
    const [hStr = '00', mStr = '00', sStr = '00'] = time.split(':');
    const month = Math.max(1, Math.min(12, Number(mm))) - 1;
    const day = Math.max(1, Math.min(31, Number(dd)));
    const year = Number(yyyy);
    const hours = Number(hStr);
    const minutes = Number(mStr);
    const seconds = Number(sStr);
    return new Date(year, month, day, hours, minutes, seconds || 0, 0);
  } catch {
    const dateTimeString = `${date} ${time}`;
    return new Date(dateTimeString);
  }
};

export const getTimeZoneIdOffline = (
  lat: number,
  lng: number
): string | null => {
  if ((!lat && lat !== 0) || (!lng && lng !== 0)) return null;
  try {
    return tzLookup(lat, lng);
  } catch (e) {
    logger.error('tz-lookup error:', e);
    return null;
  }
};

// Zone abbreviation on the service date (MM/DD/YYYY) so DST is reflected (PST vs PDT), e.g. ' (PDT)'
export const getTimeZoneLabel = (
  tz: string | null,
  serviceDate: string
): string =>
  tz ? ` (${moment.tz(serviceDate, 'MM/DD/YYYY', tz).format('z')})` : '';

// Current 'HH:mm' in the zone, only when the service date (MM/DD/YYYY) is today there; otherwise ''
export const getTimeZoneNowForDate = (
  tz: string | null,
  serviceDate: string
): string =>
  tz && moment.tz(tz).isSame(moment.tz(serviceDate, 'MM/DD/YYYY', tz), 'day')
    ? moment.tz(tz).format('HH:mm')
    : '';

// export const getEpochMsAtLocationLocalOffline = (
//   date: string, // "MM/DD/YYYY"
//   time: string, // "HH:mm"
//   lat?: number,
//   lng?: number
// ): number => {
//   try {
//     if (typeof lat === 'number' && typeof lng === 'number') {
//       const tz = getTimeZoneIdOffline(lat, lng);
//
//       if (tz) {
//         const [mm, dd, yyyy] = date.split('/');
//         const dateTimeString = `${yyyy}-${mm}-${dd} ${time}`;
//
//         const utcDate = fromZonedTime(dateTimeString, tz as unknown as string);
//
//         if (!isNaN(utcDate.getTime())) {
//           return utcDate.getTime();
//         }
//       }
//     }
//   } catch (e) {
//     logger.warn('getEpochMsAtLocationLocal fallback:', e);
//   }
//
//   return getMsFromTime(date, time);
// };

export const getEpochMsAtLocationLocalOffline = (
  date: string,
  time: string,
  lat?: number,
  lng?: number
): number => {
  try {
    if (typeof lat === 'number' && typeof lng === 'number') {
      const tz = getTimeZoneIdOffline(lat, lng);

      if (tz) {
        const [mm, dd, yyyy] = date.split('/').map((p) => p.padStart(2, '0'));
        const dateTimeString = `${yyyy}-${mm}-${dd}T${time}:00`;

        const utcDate = fromZonedTime(dateTimeString, tz);

        if (!isNaN(utcDate.getTime())) {
          return utcDate.getTime();
        }

        logger.warn(
          'getEpochMsAtLocationLocalOffline: NaN result, falling back to naive parse',
          {
            date,
            time,
            lat,
            lng,
            tz,
            dateTimeString,
          }
        );
      } else {
        logger.warn('getEpochMsAtLocationLocalOffline: no tz found', {
          lat,
          lng,
        });
      }
    }
  } catch (e) {
    logger.warn('getEpochMsAtLocationLocal fallback:', e);
  }

  return getMsFromTime(date, time);
};

export type MatrixLocation =
  | string
  | google.maps.LatLng
  | google.maps.LatLngLiteral;

export const getEncryptedHash = (url: string): [boolean, string | null] => {
  try {
    const hashPattern = /[A-Za-z0-9_-]{20,}/;
    const urlObj = new URL(url, window.location.origin);
    const segments = urlObj.pathname.split('/').filter((segment) => segment);

    for (const segment of segments) {
      if (
        hashPattern.test(segment) &&
        !['authorization', 'login', 'information', 'rides'].includes(segment)
      ) {
        return [true, segment];
      }
    }

    return [false, null];
  } catch (error) {
    logger.error('Invalid URL:', error);
    return [false, null];
  }
};

const flattenToOneLevel = (obj: any): any => {
  const result: any = {};

  const recurse = (current: any) => {
    for (const key in current) {
      if (!Object.prototype.hasOwnProperty.call(current, key)) continue;
      if (key.trim() === '') continue;

      const value = current[key];

      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        if ('standingOrder' in value) {
          result['standingOrder'] = value['standingOrder'];
        }
        recurse(value);
      } else {
        result[key] = value;
      }
    }
  };

  recurse(obj);
  return result;
};

export const encrypt = (obj: any) => {
  const xHeaderId: any = sessionSetter.getItem(X_HEADER_ID_NAME);

  const merged = { ...xHeaderId, ...obj };

  const flatObj = flattenToOneLevel(merged);

  const jsonStr = JSON.stringify(flatObj);
  const encrypted = CryptoJS.AES.encrypt(jsonStr, MASTER_KEY).toString();

  return encrypted.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const decrypt = (cipher: any) => {
  const rawCipher = typeof cipher === 'string' ? cipher : cipher?.byModuleId;

  if (!rawCipher || typeof rawCipher !== 'string') {
    return null;
  }

  try {
    const base64 = rawCipher
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(rawCipher.length + ((4 - (rawCipher.length % 4)) % 4), '=');

    if (!MASTER_KEY) throw new Error('Secret key is missing');

    const bytes = CryptoJS.AES.decrypt(base64, MASTER_KEY);
    const decryptedStr = bytes.toString(CryptoJS.enc.Utf8);

    return decryptedStr ? JSON.parse(decryptedStr) : null;
  } catch (err) {
    logger.error('Decrypt error:', err);
    return null;
  }
};

export const countMatchingDays = ({
  startDate,
  endDate,
  daysOfWeek,
}: {
  startDate?: string;
  endDate?: string;
  daysOfWeek: Array<number>;
}): number => {
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);

    let count = 0;

    while (start <= end) {
      const jsDay = start.getDay();
      const shiftedDay = (jsDay + 6) % 7;
      if (daysOfWeek.includes(shiftedDay)) {
        count++;
      }
      start.setDate(start.getDate() + 1);
    }

    return count;
  }
  return 0;
};

export const isTimeInRange = (
  start: string,
  end: string,
  check: string
): boolean => {
  const toMinutes = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  const startMin = toMinutes(start);
  const endMin = toMinutes(end);
  const checkMin = toMinutes(check);

  if (startMin <= endMin) {
    return checkMin >= startMin && checkMin <= endMin;
  }

  return checkMin >= startMin || checkMin <= endMin;
};

export const isSegmentCompleted = (statusId: idType) => {
  const nonDisableStatusIds = [
    7, 8, 9, 10, 11, 12, 13, 18, 19, 20, 25, 26, 27, 28, 32,
  ];

  return nonDisableStatusIds.includes(statusId as number);
};

export const formattedVehicleModel = (vehicle?: {
  make?: string;
  model?: string;
}) => {
  if (!vehicle) {
    return '';
  }

  const { make = '', model = '' } = vehicle;
  const symbol = make && model ? '/' : '';

  return make + symbol + model;
};

/*** function, which converts the current local time into the time in the timezone of the location ***/

const msToHHMMSS = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, '0');

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

export const getNowInTimezone = (tz: string): Date => {
  const now = new Date();

  const localeStr = now.toLocaleString('en-US', { timeZone: tz });
  return new Date(localeStr);
};

export const getTimeDifferenceInTimezone = (
  targetISO: string,
  targetTZ: string
): number => {
  const targetDate = new Date(targetISO);

  const nowInTargetTZ = getNowInTimezone(targetTZ);

  const diffMs = nowInTargetTZ.getTime() - targetDate.getTime();
  return Math.floor(diffMs / 1000);
};

export const formatTimerTime = (sec: number) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s < 10 ? '0' + s : s}`;
};

export const exactTypeRender = (service: string) => ({
  lyft: service === 'Ambulatory Door to Door' ? 'lyft_assisted' : 'lyft',
  uber: 'UberX',
  other: 'other',
});

export type WhichContractorType = {
  id: number;
  label: string;
  value: string;
};

export const RIDESHARE_CONTRACTOR_TYPES = ['uber', 'lyft'];

export type RideshareContractorType =
  (typeof RIDESHARE_CONTRACTOR_TYPES)[number];

// Contractors whose segments are sent via API and tracked in the loader popups.
// Non-rideshare entries (e.g. Bambi) still resolve to 'other' in whichContractor.
export const API_INTEGRATED_CONTRACTOR_TYPES = [
  ...RIDESHARE_CONTRACTOR_TYPES,
  'bambi',
];

const getContractorMatchName = (contractor: WhichContractorType) =>
  `${contractor.label ?? ''} ${contractor.value ?? ''}`.toLowerCase();

export const whichContractor = (
  contractor?: WhichContractorType | null
): RideshareContractorType | 'other' | null => {
  if (!contractor) {
    return null;
  }

  const contractorName = getContractorMatchName(contractor);

  return (
    RIDESHARE_CONTRACTOR_TYPES.find((contractorType) =>
      contractorName.includes(contractorType)
    ) ?? 'other'
  );
};

export const isRideshareContractor = (
  contractor?: WhichContractorType | null
) => {
  const contractorType = whichContractor(contractor);

  return RIDESHARE_CONTRACTOR_TYPES.some(
    (rideshareContractorType) => rideshareContractorType === contractorType
  );
};

export const isApiIntegratedContractor = (
  contractor?: WhichContractorType | null
) => {
  if (!contractor) {
    return false;
  }

  const contractorName = getContractorMatchName(contractor);

  return API_INTEGRATED_CONTRACTOR_TYPES.some((contractorType) =>
    contractorName.includes(contractorType)
  );
};

export const normalizeDate = (val: any): Date | null => {
  try {
    if (!val) return null;
    if (val instanceof Date) {
      const d = new Date(val.getFullYear(), val.getMonth(), val.getDate());
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof val === 'string') {
      const parts = val.split('/');
      if (parts.length === 3) {
        const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10));
        const d = new Date(yyyy, (mm || 1) - 1, dd || 1);
        return isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(val);
      return isNaN(d.getTime())
        ? null
        : new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    return null;
  } catch {
    return null;
  }
};

export const deepCheck = (obj: any) => {
  if (obj === null) return '';
  if (typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map(deepCheck);
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, deepCheck(value)])
  );
};

export type RideSegmentLite = {
  scheduledPickupTime: string | null;
  scheduledAppointmentTime: string | null;
  contractor: any;
  status: any;
  nextDay?: boolean;
};

type PickupTimeOrderError = {
  index: number;
  minAllowedTime: string;
};

const minutesToTime = (minutes: number): string => {
  const hh = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0');
  const mm = (minutes % 60).toString().padStart(2, '0');

  return `${hh}:${mm}`;
};

const COMPLETED_STATUSES = [
  7, 8, 9, 10, 11, 12, 13, 18, 19, 20, 25, 26, 27, 28,
];

export const validateScheduledPickupTimeOrder = (
  ridesSegments: RideSegmentLite[]
): PickupTimeOrderError | null => {
  let maxPreviousAppointmentTime: number | null = null;

  for (let i = 0; i < ridesSegments.length; i++) {
    const {
      scheduledPickupTime,
      scheduledAppointmentTime,
      status,
      contractor,
    } = ridesSegments[i];

    if (ridesSegments[i]?.nextDay) {
      maxPreviousAppointmentTime = null;
    }
    const CONTRACTOR = whichContractor(contractor);
    const isCompleted = [
      ...COMPLETED_STATUSES,
      ...(CONTRACTOR === 'other' ? [1, 9999] : []),
    ].includes(status.id);

    if (
      !isCompleted &&
      scheduledPickupTime &&
      maxPreviousAppointmentTime !== null
    ) {
      const [ph, pm] = scheduledPickupTime.split(':').map(Number);
      const pickupMinutes = ph * 60 + pm;

      if (pickupMinutes < maxPreviousAppointmentTime) {
        return {
          index: i,
          minAllowedTime: minutesToTime(maxPreviousAppointmentTime),
        };
      }
    }

    if (!isCompleted && scheduledAppointmentTime) {
      const [ah, am] = scheduledAppointmentTime.split(':').map(Number);
      const appointmentMinutes = ah * 60 + am;

      maxPreviousAppointmentTime =
        maxPreviousAppointmentTime === null
          ? appointmentMinutes
          : Math.max(maxPreviousAppointmentTime, appointmentMinutes);
    }
  }

  return null;
};

export const isSegmentPickupTimeInvalid = (
  segment: {
    callIndicator: boolean;
    scheduledPickupTime?: string | null; // "HH:mm"
    status: any;
    contractor: any;
  },
  coords: {
    lat: string | number;
    lng: string | number;
  },
  currentTime: string, // "HH:mm"
  rideDate: string // "MM/DD/YYYY"
) => {
  const { callIndicator, scheduledPickupTime, status, contractor } = segment;
  const CONTRACTOR = whichContractor(contractor);

  const COMBINED_FINAL_STATUS_IDS =
    CONTRACTOR === 'other'
      ? NON_RIDESHARE_FINALIZED_STATUS_IDS
      : FINALIZED_STATUS_IDS;

  if (status.id != null && COMBINED_FINAL_STATUS_IDS.has(status.id))
    return false;
  if (!callIndicator && !scheduledPickupTime) return true;
  if (callIndicator && !scheduledPickupTime) return false;

  if (scheduledPickupTime) {
    return isTimeInPastForLocation(
      coords,
      currentTime,
      scheduledPickupTime,
      rideDate
    );
  }
  return false;
};

export const isTimeInPastForLocation = (
  coords: {
    lat: string | number;
    lng: string | number;
  },
  currentTimeAtLocation: string,
  selectedTime: string, //  HH:mm
  rideDate: string // "MM/DD/YYYY"
): boolean => {
  const timeZone = tzLookup(coords.lat, coords.lng);

  const nowAtLocation = new Date(
    new Date().toLocaleString('en-US', { timeZone })
  );
  const todayAtLocationFormatted = format(nowAtLocation, 'yyyy-MM-dd');

  const [m, d, y] = rideDate.split('/');
  const pickedDateFormatted = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;

  const currentUtc = fromZonedTime(
    `${todayAtLocationFormatted}T${currentTimeAtLocation}`,
    timeZone
  );
  const selectedUtc = fromZonedTime(
    `${pickedDateFormatted}T${selectedTime}`,
    timeZone
  );
  return selectedUtc < currentUtc;
};

export const msToDateWithTimezone = (ms: number, timeZone: string): string => {
  return formatInTimeZone(new Date(ms), timeZone, 'MM/dd/yyyy');
};

export const dateKeyToUtcMs = (dateKey: string, timeZone: string): number => {
  const [month, day, year] = dateKey.split('/').map(Number);

  return fromZonedTime(new Date(year, month - 1, day), timeZone).getTime();
};

export const slugify = (sentence: string) => {
  return sentence.toLowerCase().trim().replace(/\s+/g, '-');
};

export const validateSave = ({
  openedTimeStr,
  savingTimeStr,
}: {
  openedTimeStr: string;
  savingTimeStr: string;
}) => {
  const getMinutes = (time: string) => {
    const [hrs, mins] = time?.split(':').map(Number);
    return hrs * 60 + mins;
  };

  const startMins = getMinutes(openedTimeStr);
  const endMins = getMinutes(savingTimeStr);

  let diff = endMins - startMins;

  if (diff < 0) {
    diff += 1440;
  }

  return diff <= 5 ? savingTimeStr : openedTimeStr;
};

export const isValidLevelOfService = (
  service: selectItemType,
  isContractorVersion: boolean,
  isCreate: boolean
) => {
  if (isContractorVersion) return true;
  if (!isCreate) return true;
  return defaultServices.includes(service?.value);
};

export const getTimeMinutes = (value?: string) => {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) {
    return null;
  }

  const [hours, minutes] = value.split(':').map(Number);

  if (hours > 23 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
};

export const isNotValidContractorForRideshare = (
  contractor: WhichContractorType
) => {
  const contractorType = whichContractor(contractor);
  return contractorType !== null && contractorType === 'other';
};

export const removeCounty = (sentence: string): string => {
  const countyRegex = /\bcounty\b/gi;

  return sentence.replace(countyRegex, '').replace(/\s+/g, ' ').trim();
};

export const formatTimeString = (timeStr: string): string => {
  const [hours, minutes] = timeStr.split(':').map(Number);

  if (isNaN(hours) || isNaN(minutes)) return '0 mins';

  const hourStr = hours === 1 ? '1 hr' : `${hours} hrs`;
  const minStr = minutes === 1 ? '1 min' : `${minutes} mins`;

  if (hours > 0 && minutes === 0) {
    return hourStr;
  }

  if (hours > 0 && minutes > 0) {
    return `${hourStr} ${minStr}`;
  }

  return minStr;
};

export const toTestIdSafe = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
